import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';

/**
 * Supported toast positions. These map 1:1 to the keyed <p-toast> outlets
 * declared in app.html. Passing one of these to a toast method routes the
 * message to that positioned outlet; omit it to use the default (top-right).
 */
export type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'top-center'
  | 'bottom-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'center';

/**
 * PrimeNG's <p-toast> position is a property of the toast COMPONENT, not of an
 * individual message — so a message is routed to a positioned outlet via its
 * `key`. app.html declares one keyless <p-toast> (default top-right) plus one
 * keyed <p-toast> per position; this maps a requested position to that key.
 */
const POSITION_KEY: Record<ToastPosition, string | undefined> = {
  'top-right': undefined,          // default keyless outlet
  'top-left': 'tl',
  'top-center': 'tc',
  'bottom-right': 'br',
  'bottom-left': 'bl',
  'bottom-center': 'bc',
  'center': 'c',
};

@Injectable({ providedIn: 'root' })
export class ToastService {
  constructor(private messageService: MessageService) {}

  /** Resolve the outlet key for an optional position (undefined => default outlet). */
  private keyFor(position?: ToastPosition): string | undefined {
    return position ? POSITION_KEY[position] : undefined;
  }

  success(detail: string, summary: string = 'Success', position?: ToastPosition): void {
    this.messageService.add({ severity: 'success', summary, detail, life: 4000, key: this.keyFor(position) });
  }

  error(detail: string, summary: string = 'Error', position?: ToastPosition): void {
    this.messageService.add({ severity: 'error', summary, detail, life: 6000, key: this.keyFor(position) });
  }

  warning(detail: string, summary: string = 'Warning', position?: ToastPosition): void {
    this.messageService.add({ severity: 'warn', summary, detail, life: 5000, key: this.keyFor(position) });
  }

  info(detail: string, summary: string = 'Info', position?: ToastPosition): void {
    this.messageService.add({ severity: 'info', summary, detail, life: 4000, key: this.keyFor(position) });
  }
}
